import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    Camera,
    Star,
    Sparkles,
    User as UserIcon,
    PenLine,
    MessageSquareQuote,
    PackageCheck,
    Package,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
    fetchMyReviews,
    uploadImage,
    updateMyProfile,
    fetchMyRentals,
    fetchMyListings,
} from "../lib/api";

const PROFILE = {
    root: "profile-page",
    editLink: "profile-edit-link",
    pictureBtn: "profile-picture-btn",
    pictureInput: "profile-picture-file",
    reviewsList: "profile-reviews-list",
    reviewsEmpty: "profile-reviews-empty",
    rentalsList: "profile-rentals-list",
    rentalsEmpty: "profile-rentals-empty",
    listingsList: "profile-listings-list",
    listingsEmpty: "profile-listings-empty",
    kpiReviews: "profile-kpi-reviews",
    kpiRentals: "profile-kpi-rentals",
    kpiListings: "profile-kpi-listings",
};

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

const formatDateShort = (iso) => {
    if (!iso) return "";
    try {
        const [y, m, d] = iso.split("T")[0].split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

const StarRow = ({ value }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star
                key={n}
                className={`h-4 w-4 ${n <= value ? "fill-[#d6a73f] text-[#d6a73f]" : "text-[#2e3220]/25"}`}
                strokeWidth={2}
            />
        ))}
    </div>
);

const ReviewRow = ({ r }) => {
    const initial = (r.reviewer_name || "N").charAt(0).toUpperCase();
    const isFromOwner = r.direction === "owner_to_renter";
    return (
        <li className="flex gap-3 rounded-2xl border border-[#2e3220]/8 bg-[#F4F1EA] p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#737a3d] font-display text-sm font-bold text-[#F4F1EA]">
                {r.reviewer_picture ? (
                    <img
                        src={r.reviewer_picture}
                        alt={r.reviewer_name}
                        className="h-full w-full rounded-full object-cover"
                    />
                ) : (
                    initial
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-semibold text-[#2e3220]">
                        {r.reviewer_name || "Neighbor"}
                    </p>
                    <span className="rounded-full bg-[#737a3d]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#737a3d]">
                        {isFromOwner ? "As a renter" : "As a lender"}
                    </span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                    <StarRow value={r.rating || 0} />
                    <span className="text-xs text-[#5a6142]">· {r.item_name}</span>
                </div>
                {r.comment && (
                    <p className="mt-2 text-sm text-[#5a6142]">“{r.comment}”</p>
                )}
            </div>
        </li>
    );
};

const RentalRow = ({ r }) => (
    <li className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#2e3220]/8 bg-[#F4F1EA] p-4">
        {r.item_image ? (
            <img
                src={r.item_image}
                alt={r.item_name}
                className="h-14 w-14 shrink-0 rounded-2xl object-cover"
            />
        ) : (
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                <PackageCheck className="h-6 w-6" strokeWidth={2} />
            </div>
        )}
        <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#2e3220]">
                {r.item_name || "Rental"}
            </p>
            <p className="mt-0.5 text-xs text-[#5a6142]">
                {formatDateShort(r.start_date)} → {formatDateShort(r.end_date)}
                {r.owner_name ? ` · from ${r.owner_name}` : ""}
            </p>
        </div>
        <span className="rounded-full bg-[#737a3d]/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
            {formatMoney(r.total)}
        </span>
    </li>
);

const ListingRow = ({ item }) => {
    const statusLabel =
        item.status === "rented"
            ? "Rented right now"
            : item.status === "inactive"
            ? "Paused"
            : "Available";
    const statusClass =
        item.status === "rented"
            ? "bg-[#B56C5A]/15 text-[#B56C5A]"
            : item.status === "inactive"
            ? "bg-[#2e3220]/10 text-[#2e3220]/80"
            : "bg-[#737a3d]/15 text-[#737a3d]";
    return (
        <li className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#2e3220]/8 bg-[#F4F1EA] p-4">
            {item.images?.[0] ? (
                <img
                    src={item.images[0]}
                    alt={item.name}
                    className="h-14 w-14 shrink-0 rounded-2xl object-cover"
                />
            ) : (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                    <Package className="h-6 w-6" strokeWidth={2} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#2e3220]">
                    {item.name}
                </p>
                <p className="mt-0.5 text-xs text-[#5a6142]">
                    {formatMoney(item.price_per_day)} / day
                    {item.park ? ` · ${item.park}` : ""}
                </p>
            </div>
            <span
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${statusClass}`}
            >
                {statusLabel}
            </span>
            <Link
                to="/dashboard/listings"
                className="text-xs font-semibold text-[#737a3d] hover:underline"
            >
                Manage
            </Link>
        </li>
    );
};

const KpiToggle = ({ label, value, icon: Icon, active, onClick, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-pressed={active}
        className={`group relative flex flex-1 flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#737a3d] ${
            active
                ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-[0_10px_30px_-15px_rgba(115,122,61,0.6)]"
                : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40 hover:bg-[#EFEAD8]"
        }`}
    >
        <div className="flex items-center gap-2">
            <Icon
                className={`h-4 w-4 ${active ? "text-[#F4F1EA]" : "text-[#737a3d]"}`}
                strokeWidth={2.5}
            />
            <span
                className={`text-[10px] font-bold uppercase tracking-widest ${active ? "text-[#F4F1EA]/85" : "text-[#737a3d]"}`}
            >
                {label}
            </span>
        </div>
        <span
            className={`font-display text-3xl font-bold leading-none ${active ? "text-[#F4F1EA]" : "text-[#2e3220]"}`}
        >
            {value}
        </span>
        <span
            className={`absolute bottom-2 right-3 text-[10px] font-semibold ${active ? "text-[#F4F1EA]/80" : "text-[#5a6142]/70 opacity-0 group-hover:opacity-100"}`}
        >
            {active ? "Showing" : "Tap to view"}
        </span>
    </button>
);

const Profile = () => {
    const { user, setUser } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(true);
    const [rentals, setRentals] = useState([]);
    const [rentalsLoading, setRentalsLoading] = useState(true);
    const [listings, setListings] = useState([]);
    const [listingsLoading, setListingsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("reviews");
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!user?.user_id) return;
        setReviewsLoading(true);
        fetchMyReviews(user.user_id)
            .then((list) => setReviews(list || []))
            .catch(() => setReviews([]))
            .finally(() => setReviewsLoading(false));
    }, [user?.user_id]);

    useEffect(() => {
        if (!user?.user_id) return;
        setRentalsLoading(true);
        fetchMyRentals()
            .then((data) => {
                // Backend returns a bucketed shape: {pending, awaiting_payment,
                // active, return_pending, upcoming, past, declined, total}.
                // Flatten so completedRentals filter (status === "completed")
                // works — completed rentals live in the `past` bucket.
                const buckets = data || {};
                const all = [
                    ...(buckets.pending || []),
                    ...(buckets.awaiting_payment || []),
                    ...(buckets.active || []),
                    ...(buckets.return_pending || []),
                    ...(buckets.upcoming || []),
                    ...(buckets.past || []),
                    ...(buckets.declined || []),
                ];
                setRentals(all);
            })
            .catch(() => setRentals([]))
            .finally(() => setRentalsLoading(false));

        setListingsLoading(true);
        fetchMyListings()
            .then((list) => setListings(Array.isArray(list) ? list : []))
            .catch(() => setListings([]))
            .finally(() => setListingsLoading(false));
    }, [user?.user_id]);

    const completedRentals = useMemo(
        () => rentals.filter((r) => r.status === "completed"),
        [rentals],
    );
    const activeListings = useMemo(
        () =>
            listings.filter(
                (it) => it.status === "active" || it.status === "rented",
            ),
        [listings],
    );

    if (!user) return null;

    const initials = (user.name || "N")
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    const avg =
        reviews.length > 0
            ? (
                  reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) /
                  reviews.length
              ).toFixed(1)
            : null;

    const handlePicture = async (file) => {
        if (!file) return;
        try {
            setUploading(true);
            const { url } = await uploadImage(file);
            const fresh = await updateMyProfile({ picture: url });
            setUser(fresh);
            toast.success("Profile picture updated");
        } catch (err) {
            toast.error("Couldn't upload picture", {
                description:
                    err?.response?.data?.detail ||
                    "Try a smaller JPG/PNG (max 8 MB).",
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <main
            data-testid={PROFILE.root}
            className="relative z-10 mx-auto max-w-4xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Your profile
                </span>
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                {user.name || "Your profile"}
            </h1>
            <p className="mt-2 text-base text-[#5a6142]">
                This is what other neighbors see. Add a photo, mention your park,
                and read what people say about you.
            </p>

            {/* Avatar + upload */}
            <section className="mt-8 flex items-center gap-5 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6">
                <div className="relative">
                    <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-[#737a3d] font-display text-2xl font-bold text-[#F4F1EA]">
                        {user.picture ? (
                            <img
                                src={user.picture}
                                alt={user.name}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            initials || <UserIcon className="h-8 w-8" />
                        )}
                    </div>
                    {uploading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                            <Loader2 className="h-5 w-5 animate-spin text-white" />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#2e3220]">
                        {user.name}
                    </p>
                    <p className="text-xs text-[#5a6142]">{user.email}</p>
                    {avg && (
                        <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#737a3d]">
                            <Star className="h-3 w-3 fill-[#d6a73f] text-[#d6a73f]" />
                            {avg} · {reviews.length} review{reviews.length === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    data-testid={PROFILE.pictureInput}
                    onChange={(e) => {
                        handlePicture(e.target.files?.[0]);
                        e.target.value = "";
                    }}
                    className="hidden"
                />
                <button
                    type="button"
                    data-testid={PROFILE.pictureBtn}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-ghost self-center"
                >
                    <Camera className="h-4 w-4" strokeWidth={2.5} />
                    Change photo
                </button>
            </section>

            {/* KPI toggles */}
            <section className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <KpiToggle
                    label="Reviews"
                    value={reviews.length}
                    icon={MessageSquareQuote}
                    active={activeTab === "reviews"}
                    onClick={() => setActiveTab("reviews")}
                    testId={PROFILE.kpiReviews}
                />
                <KpiToggle
                    label="Rentals completed"
                    value={completedRentals.length}
                    icon={PackageCheck}
                    active={activeTab === "rentals"}
                    onClick={() => setActiveTab("rentals")}
                    testId={PROFILE.kpiRentals}
                />
                <KpiToggle
                    label="Active listings"
                    value={activeListings.length}
                    icon={Package}
                    active={activeTab === "listings"}
                    onClick={() => setActiveTab("listings")}
                    testId={PROFILE.kpiListings}
                />
            </section>

            {/* Edit profile link */}
            <section className="mt-6 flex flex-wrap items-center justify-end gap-2">
                <Link
                    to={`/neighbors/${user.user_id}`}
                    className="btn-ghost"
                >
                    <UserIcon className="h-4 w-4" strokeWidth={2.5} />
                    View public page
                </Link>
                <Link
                    to="/profile/edit"
                    data-testid={PROFILE.editLink}
                    className="btn-primary"
                >
                    <PenLine className="h-4 w-4" strokeWidth={2.5} />
                    Edit profile
                </Link>
            </section>

            {/* Tab content */}
            {activeTab === "reviews" && (
                <section className="mt-6 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6">
                    <div className="flex items-baseline justify-between">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            What neighbors say about you
                        </p>
                        <p className="text-xs text-[#5a6142]">
                            {reviews.length} review{reviews.length === 1 ? "" : "s"}
                        </p>
                    </div>
                    {reviewsLoading ? (
                        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-[#E8E2D0]" />
                    ) : reviews.length === 0 ? (
                        <p
                            data-testid={PROFILE.reviewsEmpty}
                            className="mt-4 text-sm text-[#5a6142]/80"
                        >
                            You haven&apos;t received any reviews yet — complete a
                            rental as either a lender or renter and reviews will show
                            up here.
                        </p>
                    ) : (
                        <ul
                            data-testid={PROFILE.reviewsList}
                            className="mt-4 space-y-3"
                        >
                            {reviews.map((r) => (
                                <ReviewRow key={r.id} r={r} />
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {activeTab === "rentals" && (
                <section className="mt-6 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6">
                    <div className="flex items-baseline justify-between">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            Rentals you&apos;ve completed
                        </p>
                        <p className="text-xs text-[#5a6142]">
                            {completedRentals.length} completed
                        </p>
                    </div>
                    {rentalsLoading ? (
                        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-[#E8E2D0]" />
                    ) : completedRentals.length === 0 ? (
                        <p
                            data-testid={PROFILE.rentalsEmpty}
                            className="mt-4 text-sm text-[#5a6142]/80"
                        >
                            No completed rentals yet.{" "}
                            <Link to="/browse" className="font-semibold text-[#737a3d] hover:underline">
                                Browse items
                            </Link>{" "}
                            to book your first rental.
                        </p>
                    ) : (
                        <ul
                            data-testid={PROFILE.rentalsList}
                            className="mt-4 space-y-3"
                        >
                            {completedRentals.map((r) => (
                                <RentalRow key={r.id} r={r} />
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {activeTab === "listings" && (
                <section className="mt-6 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6">
                    <div className="flex items-baseline justify-between">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            Your active listings
                        </p>
                        <p className="text-xs text-[#5a6142]">
                            {activeListings.length} live
                        </p>
                    </div>
                    {listingsLoading ? (
                        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-[#E8E2D0]" />
                    ) : activeListings.length === 0 ? (
                        <p
                            data-testid={PROFILE.listingsEmpty}
                            className="mt-4 text-sm text-[#5a6142]/80"
                        >
                            You don&apos;t have any active listings yet.{" "}
                            <Link to="/list-item" className="font-semibold text-[#737a3d] hover:underline">
                                List an item
                            </Link>{" "}
                            to start earning.
                        </p>
                    ) : (
                        <ul
                            data-testid={PROFILE.listingsList}
                            className="mt-4 space-y-3"
                        >
                            {activeListings.map((it) => (
                                <ListingRow key={it.id} item={it} />
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </main>
    );
};

export default Profile;
